import {
  runOrbieeChatTurn,
  getOrbieeDeviceId,
  formatConversationForDisplay,
  isEmptyTranscriptError,
} from "../../services/websocket";
import { showAlert } from "../../utils/alertPopupManager";
import {
  markAgentNavigation,
  beginAgentAction,
  endAgentAction,
} from "../../services/uiAgentSync";
import { extractAgentBillingFromResponse } from "../../utils/agentBillingPayload";


export default async function OrbieeFlow(
  messages,
  customerId,
  navigate = null,
  addToCart = null,
  languageCode = "eng",
  onLastResponse = null,
  options = null
) {
  try {
    // UI → Agent sync (echo suppression): every navigation this flow performs
    // is agent-driven — mark it so the resulting view_changed/product_viewed
    // effect events are tagged source: "agent_navigation", not manual_click.
    if (typeof navigate === "function") {
      const rawNavigate = navigate;
      navigate = (...args) => {
        markAgentNavigation();
        return rawNavigate(...args);
      };
    }

    let intent_override = null;
    if (sessionStorage.getItem("orbiee_size_confirmation") === "true") {
      intent_override = "add_to_cart";
    }
    let user_answer = sessionStorage.getItem("user_answer");
    // Session storage key for conversation_id (per customer)
    const conversationIdKey = `orbiee_conversation_id_${customerId}`;

    // Get existing conversation_id from session storage
    let conversationId = sessionStorage.getItem(conversationIdKey);
    let lastResponse = null;

    // If no messages or the array is empty, return null or undefined (no action)

    const msgObj = Array.isArray(messages) ? messages[0] : messages;
    const audioBlob =
      msgObj?.audioBlob instanceof Blob || msgObj?.audioBlob instanceof File
        ? msgObj.audioBlob
        : null;

    // Text path: transcription string, chat content, or legacy STT result object
    let userQuery = null;
    if (!audioBlob) {
      if (msgObj?.transcription) {
        userQuery = msgObj.transcription;
      } else {
        userQuery = msgObj?.content;
      }
    }

    const userId =
      customerId != null && customerId !== "" ? String(customerId) : "guest";
    const wsQuery = {};
    if (conversationId) {
      wsQuery.ctx_conversation_id = String(conversationId);
    }
    if (intent_override != null && intent_override !== "") {
      wsQuery.ctx_intent_override = String(intent_override);
    }
    if (user_answer != null && user_answer !== "") {
      wsQuery.ctx_user_answer = String(user_answer);
    }
    const flowOptions = options && typeof options === "object" ? options : {};
    const langForTurn =
      flowOptions.languageCode != null && flowOptions.languageCode !== ""
        ? flowOptions.languageCode
        : languageCode;

    // Interaction mode for the WS frame (text | voice | avatar). Callers that
    // know the UI surface should pass options.mode; otherwise derive from payload.
    const turnMode =
      flowOptions.mode === "text" ||
      flowOptions.mode === "voice" ||
      flowOptions.mode === "avatar"
        ? flowOptions.mode
        : audioBlob
          ? "voice"
          : "text";

    lastResponse = await runOrbieeChatTurn({
      userId,
      deviceId: await getOrbieeDeviceId(),
      message: userQuery,
      audioBlob: audioBlob || undefined,
      languageCode: langForTurn,
      queryParams: wsQuery,
      onLlmDisplayUpdate: flowOptions.onLlmDisplayUpdate,
      onTranscript: flowOptions.onTranscript,
      onLlmResponse: flowOptions.onLlmResponse,
      mode: turnMode,
    });
    sessionStorage.setItem("user_answer", "");
    sessionStorage.setItem("orbiee_size_confirmation", "false");
    console.log("lastResponse", lastResponse);
    // Store conversation_id in session storage if we got one from the API
    if (lastResponse && lastResponse.conversation_id) {
      conversationId = lastResponse.conversation_id;
      sessionStorage.setItem(conversationIdKey, conversationId);
    }

    // Sync UI state (cart / wishlist) BEFORE navigation so /cart and /checkout
    // render the agent snapshot immediately.
    if (typeof onLastResponse === "function" && lastResponse) {
      try {
        onLastResponse(lastResponse);
      } catch (err) {
        console.warn("[OrbieeFlow] onLastResponse threw", err);
      }
    }

    // Check intent and handle navigation before returning.
    // Cart mutations applied inside this block come from the agent's response —
    // beginAgentAction ensures their ui_events are tagged source: "agent".
    beginAgentAction();
    try {
    if (lastResponse) {
      const intentRaw = lastResponse.intent;
      const intent = Array.isArray(intentRaw)
        ? intentRaw
        : intentRaw != null && intentRaw !== ""
          ? [String(intentRaw)]
          : [];
      const products = lastResponse.products || [];

      if (intent.includes("detail_page")) {
          // Handle detail page intent
          const action = lastResponse.action || {};
          const actionProduct = action.product || [];

          // Get product from action.product (preferred) or products array
          const detailProduct =
            Array.isArray(actionProduct) && actionProduct.length > 0
              ? actionProduct[0]
              : products.length > 0
                ? products[0]
                : null;

          if (detailProduct && detailProduct.id) {
            // Check if chat box is currently open and not manually closed
            const chatIsOpen =
              sessionStorage.getItem("orbiee_chat_is_open") === "true";
            const wasManuallyClosed =
              sessionStorage.getItem("orbiee_chat_manually_closed") === "true";

            // Store chat state before navigation (only if chat was open and not manually closed)
            if (chatIsOpen && !wasManuallyClosed) {
              sessionStorage.setItem("orbiee_chat_should_be_open", "true");
            }

            const productDetailFromAgent =
              lastResponse.action?.product_detail ??
              lastResponse.product_detail ??
              null;

            // Navigate to item detail page with product data
            if (navigate && typeof navigate === "function") {
              navigate(`/item/${detailProduct.id}`, {
                state: {
                  product: detailProduct,
                  productDetail: productDetailFromAgent,
                },
              });
            }
          }
        } else if (intent.includes("view_wishlist")) {
          if (navigate && typeof navigate === "function") {
            navigate("/wishlist");
          }
        } else if (intent.includes("remove_from_wishlist")) {
          // Mutation-only — WishlistContext synced via applyWishlistFromAgentResponse
        } else if (intent.includes("add_to_wishlist")) {
          // State + navigation after applyWishlistFromAgentResponse (onLastResponse)
        } else if (intent.includes("view_cart")) {
          // Don't navigate to cart if the agent was actually answering about wishlist.
          const isWishlistText = /wishlist/i.test(lastResponse.conversation_text || "");
          if (!isWishlistText && navigate && typeof navigate === "function") {
            navigate("/cart");
          }
        } else if (intent.includes("remove_from_cart") || intent.includes("clear_cart")) {
          // Cart mutation — cart state is synced via tryReplaceCartFromAgentAction in ChatBot.js; no navigation needed.
        } else if (intent.includes("add_to_cart")) {
          sessionStorage.setItem("orbiee_virtual_try_on_open", "false")
          if (lastResponse.action?.status === "PENDING_SIZE_CONFIRMATION") {







            try {
              //  showConfirmationPopup({
              //   title: "Size Confirmation",
              //   // message: `Do you want to add ${productName} in size ${size} to your cart?`,
              //   message: "would you like to add this product in the suggested size?",
              //   customerId: customerId ,
              //   navigate: navigate,
              //   addToCart: addToCart
              // });
              sessionStorage.setItem("orbiee_size_confirmation", "true");
              return lastResponse.conversation_text;


              // Call OrbieeFlow recursively with the user's selection
              // const responseMessage = {
              //   content: result,
              //   transcription: result,
              // };

              // // Recursively call OrbieeFlow with the result to continue the conversation
              // return await OrbieeFlow(
              //   responseMessage,
              //   customerId,
              //   navigate,
              //   addToCart
              // );
            } catch (error) {
              console.error("Error showing confirmation popup:", error);
            }
          } else {
            // Agent already mutated the server cart and returned a snapshot.
            // UI cart is applied via applyCartFromAgentResponse in onLastResponse —
            // do NOT call addToCart (that would hit the Cart API again).
            const action = lastResponse.action || {};
            const cartAddSucceeded =
              action.status === "SUCCESS" && action.type === "ADD_TO_CART";
            const hasCartSnapshot =
              (lastResponse.cart &&
                Array.isArray(lastResponse.cart.items) &&
                lastResponse.cart.items.length > 0) ||
              (Array.isArray(action.cart_data) && action.cart_data.length > 0);
            if (
              (cartAddSucceeded || hasCartSnapshot) &&
              navigate &&
              typeof navigate === "function"
            ) {
              navigate("/cart");
            }
          }
        } else if (intent.includes("billing_confirmation")) {
          // Handle billing confirmation — redirect to checkout with agent lines + totals
          const action = lastResponse.action || {};
          const { billing, lines, totals } =
            extractAgentBillingFromResponse(lastResponse);
          const billingDetails = billing || action.billing_details || {};
          const cartData = action.cart_data || lines || [];

          // Check if chat box is currently open and not manually closed
          const chatIsOpen =
            sessionStorage.getItem("orbiee_chat_is_open") === "true";
          const wasManuallyClosed =
            sessionStorage.getItem("orbiee_chat_manually_closed") === "true";

          if (chatIsOpen && !wasManuallyClosed) {
            sessionStorage.setItem("orbiee_chat_should_be_open", "true");
          }

          if (navigate && typeof navigate === "function") {
            navigate("/checkout", {
              state: {
                isAgentCheckout: true,
                fromChat: true,
                cartData,
                billingDetails,
                lines,
                totals,
              },
            });
          }
        } else if (intent.includes("confirm_purchase")) {
          // Handle confirm purchase intent - redirect to order confirmation page
          const action = lastResponse.action || {};
          const summary =
            lastResponse.summary != null && typeof lastResponse.summary === "object"
              ? lastResponse.summary
              : {};
          const totalAmount =
            action.total_amount ??
            summary.total ??
            lastResponse.total ??
            0;
          const purchasedItems = Array.isArray(action.purchased_items)
            ? action.purchased_items
            : Array.isArray(summary.items)
              ? summary.items
              : [];
          const { lines, totals } = extractAgentBillingFromResponse(lastResponse);
          const articleIds = Array.isArray(action.article_ids)
            ? action.article_ids
            : Array.isArray(summary.article_ids)
              ? summary.article_ids
              : Array.isArray(summary.order_ids)
                ? summary.order_ids.map(String)
                : [];

          // Check if chat box is currently open and not manually closed
          const chatIsOpen =
            sessionStorage.getItem("orbiee_chat_is_open") === "true";
          const wasManuallyClosed =
            sessionStorage.getItem("orbiee_chat_manually_closed") === "true";

          if (chatIsOpen && !wasManuallyClosed) {
            sessionStorage.setItem("orbiee_chat_should_be_open", "true");
          }

          if (navigate && typeof navigate === "function") {
            navigate("/order-confirmation", {
              state: {
                isAgentFlow: true,
                fromChat: true,
                message:
                  lastResponse.conversation_text ||
                  "Your purchase has been completed by Orbiee Assistant.",
                totalAmount: totals?.total ?? totalAmount,
                purchasedItems,
                articleIds,
                summary,
                lines,
                totals,
                currency: totals?.currency || summary.currency || "USD",
              },
            });
          }
        } else if (
          products.length > 0 &&
          (!intent.length ||
            intent.includes("self") ||
            intent.includes("uncertain") ||
            intent.includes("others") ||
            intent.includes("search") ||
            intent.includes("product_list"))
        ) {
          // Check if chat box is currently open and not manually closed
          const chatIsOpen =
            sessionStorage.getItem("orbiee_chat_is_open") === "true";
          const wasManuallyClosed =
            sessionStorage.getItem("orbiee_chat_manually_closed") === "true";

          // Store chat state before navigation (only if chat was open and not manually closed)
          if (chatIsOpen && !wasManuallyClosed) {
            sessionStorage.setItem("orbiee_chat_should_be_open", "true");
          }

          // Store products in sessionStorage for the recommendations page
          sessionStorage.setItem(
            "orbiee_recommendation_products",
            JSON.stringify(products)
          );

          // Dispatch custom event to notify recommendations page of new products
          // This ensures updates even when already on the same page
          try {
            window.dispatchEvent(
              new CustomEvent("orbiee:productsUpdated", {
                detail: { products, timestamp: Date.now() },
              })
            );
          } catch (e) {
            console.warn("Failed to dispatch products update event", e);
          }

          // Navigate to recommendations page using React Router (no page reload)
          // Add timestamp to force update when already on the same page
          if (navigate && typeof navigate === "function") {
            navigate("/recommendations", {
              state: {
                products,
                timestamp: Date.now(), // Force update when navigating to same route
              },
              replace: false, // Ensure it's treated as a new navigation
            });
          }
        } else if (intent.includes("order_history")) {
          if (navigate && typeof navigate === "function") {
            navigate("/order-history");
          }
        } else if (
          intent.includes("update_cart_quantity") ||
          intent.includes("session_restore") ||
          intent.includes("cache_sync")
        ) {
          // Silent operations — no navigation; ChatBot handles toast/state sync
        } else if (intent.includes("virtual_try_on")) {
          // Handle virtual try-on intent - open VirtualTryOn component with product_id
          const action = lastResponse.action || {};
          const productId = action.product_id || null;

          if (productId) {
            // The VTO overlay opens via sessionStorage polling — mark it now so
            // the resulting view_changed(vto) is tagged as agent navigation.
            markAgentNavigation();
            // Store product_id in sessionStorage to trigger VirtualTryOn opening
            sessionStorage.setItem(
              "orbiee_virtual_try_on_product_id",
              productId
            );
            sessionStorage.setItem("orbiee_virtual_try_on_open", "true");
            // Indicate that we should auto-select the profile picture option
            sessionStorage.setItem(
              "orbiee_virtual_try_on_autoclick_profile",
              "true"
            );
          }
        } else if (intent.includes("close_virtual_try_on")) {
          // Handle close virtual try-on intent - close VirtualTryOn component
          try {
            // Mark virtual try-on as closed (agent-driven — tag the effect event)
            markAgentNavigation();
            sessionStorage.setItem("orbiee_virtual_try_on_open", "false");
            // Optionally clear associated data
            sessionStorage.removeItem("orbiee_virtual_try_on_product_id");
            sessionStorage.removeItem("orbiee_virtual_try_on_autoclick_profile");
          } catch (e) {
            console.warn("Failed to close virtual try-on state", e);
          }
        } else if (intent.includes("price_match")) {
          // Handle price match intent — show alert via AlterPopup
          // Only show alerts for voice/avatar flows (where we have a transcription),
          // not for normal chat text requests.
          const isVoiceOrAvatar = !!audioBlob || !!msgObj?.transcription;
          const action = lastResponse.action || {};
          if (isVoiceOrAvatar && action.status === "SUCCESS") {
            showAlert({
              severity: "success",
              message: `Price matched! New price: ${action.currency} ${action.matched_price} (matched with ${action.competitor})`,
            });
          } else if (isVoiceOrAvatar && action.status === "REJECTED") {
            showAlert({
              severity: "error",
              message: action.reason || "Unable to match the price at this time.",
            });
          } else if (isVoiceOrAvatar && action.status === "ERROR") {
            showAlert({
              severity: "warning",
              message: action.message || "Could not process the price match request.",
            });
          } else if (isVoiceOrAvatar && action.status === "FOLLOW_UP_REQUIRED") {
            const raw = action.raw || {};
            showAlert({
              severity: "info",
              message: raw.message || lastResponse.conversation_text || "Please share the store and price you saw so we can verify.",
            });
          }
        }
    }
    } finally {
      endAgentAction();
    }

    // Return display text (deduped; follow-up appended only when not already in body)
    if (lastResponse?.conversation_text !== null) {
      return formatConversationForDisplay(
        lastResponse.conversation_text,
        lastResponse.followup
      );
    }
    if (lastResponse?.followup) {
      const followupOnly = formatConversationForDisplay("", lastResponse.followup);
      if (followupOnly) {
        sessionStorage.setItem("user_answer", followupOnly);
      }
      return followupOnly;
    }
    // Fallback: return null if no conversation_text found
    return "Excuse me! Can you come again please?";
  } catch (error) {
    // Soft voice failures (mic closed with silence) — don't treat as a hard crash.
    if (isEmptyTranscriptError(error)) {
      console.debug("[OrbieeFlow] empty transcript — soft cancel");
      throw error;
    }
    console.error("Error in OrbieeFlow:", error);
    throw error;
  }
}

